import { ReactNode } from "react";
import { Button, Tooltip, TooltipContent, TooltipTrigger, useSidebar } from "@nfp-tracker/components";
import { ChevronsLeft, ChevronsRight, PanelLeft } from "lucide-react";

type AppPageHeaderProps = {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
};

const SidebarHeaderToggle = ({ className }: { className?: string }) => {
  const { isMobile, state, toggleSidebar } = useSidebar();
  const isExpanded = state === "expanded";

  const label = isMobile
    ? "Open navigation"
    : isExpanded
      ? "Collapse sidebar"
      : "Expand sidebar";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={className}
          onClick={toggleSidebar}
          aria-label={label}
        >
          {isMobile ? (
            <PanelLeft className="h-4 w-4" />
          ) : isExpanded ? (
            <ChevronsLeft className="h-4 w-4" />
          ) : (
            <ChevronsRight className="h-4 w-4" />
          )}
          <span className="sr-only">{label}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
};

export const AppPageHeader = ({
  title,
  subtitle,
  actions,
}: AppPageHeaderProps) => {
  return (
    <header className="sticky top-0 border-b border-border/70 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="flex w-full flex-row items-center p-3">
        <div className="basis-[calc((100%-48rem)/2)] justify-start">
          <SidebarHeaderToggle className="h-9 w-9" />
        </div>
        <div className="min-w-0 max-w-3xl grow-0 shrink basis-[48rem] pl-3 pr-0 md:pl-5 md:pr-5 flex flex-row items-center justify-between">
          <div>
            <h1 className="truncate text-lg font-semibold md:text-xl">
              {title}
            </h1>
            {subtitle ? (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            ) : null}
          </div>
          {actions ? (
            <div className="ml-auto flex items-center gap-2 basis-[calc((100%-48rem)/2)]">
              {actions}
            </div>
          ) : (
            <div
              className="hidden h-9 w-9 md:block ml-auto basis-[calc((100%-48rem)/2)]"
              aria-hidden="true"
            />
          )}
        </div>
      </div>
    </header>
  );
};
